<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview
        :title="isEdit ? 'Sửa yêu cầu bổ sung ngân sách' : 'Tạo yêu cầu bổ sung ngân sách'"
      >
        <template v-slot:preview>
          <b-form @submit.prevent="onSaveDraft">
            <!-- Banner trạng thái cho phiếu APPROVED — chỉ thông tin -->
            <!-- Defensive: phiếu legacy có kỳ không thuộc năm hiện tại -->
            <b-alert
              v-if="hasOutOfYearPeriods"
              show
              variant="danger"
              class="mb-3"
            >
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Phiếu có kỳ không thuộc năm {{ currentYear }}. Chỉ cho phép bổ sung ngân sách
              năm hiện tại — vui lòng xoá các kỳ ngoài năm trước khi lưu.
            </b-alert>

            <b-row class="mb-3">
              <!-- Doanh nghiệp -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Doanh nghiệp <span class="text-danger">*</span>
                  </template>
                  <b-form-select
                    v-model="form.companyId"
                    :options="companyOptions"
                    :disabled="formDisabled"
                    size="sm"
                  />
                </b-form-group>
              </b-col>

              <!-- Phòng ban -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Phòng ban <span class="text-danger">*</span>
                  </template>
                  <Autosuggest
                    :model="departmentModel"
                    :suggestions="departmentSuggestions"
                    suggestionName="name"
                    placeholder="phòng ban"
                    :limit="50"
                    :disabled="formDisabled"
                    @select="onSelectedDepartment"
                    @change="onInputDepartment"
                  >
                    <template #custom="{ suggestion }">{{
                      suggestion.item.name
                    }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>

              <!-- Cửa hàng -->
              <b-col cols="12" md="3">
                <b-form-group>
                <template #label>
                  Cửa hàng <span class="text-danger">*</span>
                </template>
                  <Autosuggest
                    :model="storeModel"
                    :suggestions="storeSuggestions"
                    suggestionName="name"
                    placeholder="cửa hàng"
                    :limit="50"
                    :disabled="formDisabled"
                    @select="onSelectedStore"
                    @change="onInputStore"
                  >
                    <template #custom="{ suggestion }">{{
                      suggestion.item.name
                    }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>

              <!-- Khoản mục phí -->
              <b-col cols="12" md="3">
                <b-form-group>
                  <template #label>
                    Khoản mục phí <span class="text-danger">*</span>
                  </template>
                  <Autosuggest
                    :model="costItemModel"
                    :suggestions="costItemSuggestions"
                    suggestionName="name"
                    placeholder="khoản mục phí"
                    :limit="50"
                    :disabled="formDisabled"
                    @select="onSelectedCostItem"
                    @change="onInputCostItem"
                  >
                    <template #custom="{ suggestion }">{{
                      suggestion.item.name
                    }}</template>
                  </Autosuggest>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- KHU VỰC ĐA KỲ — bảng inline đơn giản -->
            <b-row class="mb-2">
              <b-col>
                <div class="d-flex align-items-center justify-content-between">
                  <h6 class="mb-0">
                    Phân bổ theo kỳ
                    <b-badge variant="info" class="ml-1">{{ form.periods.length }}/{{ PERIOD_CAP }} kỳ</b-badge>
                    <b-badge v-if="totalAmount > 0" variant="success" class="ml-1">
                      Tổng: {{ convertPrice(totalAmount) }} đ
                    </b-badge>
                  </h6>
                </div>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col cols="12" md="10">
                <b-table-simple
                  small
                  bordered
                  responsive
                  class="period-table mb-1"
                >
                  <b-thead>
                    <b-tr>
                      <b-th style="width:50px" class="text-center">#</b-th>
                      <b-th style="width:140px">Tháng</b-th>
                      <b-th style="width:140px">Năm</b-th>
                      <b-th>Số tiền (đ)</b-th>
                      <b-th style="width:80px" class="text-center">Thao tác</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr
                      v-for="(period, idx) in form.periods"
                      :key="`period-${idx}`"
                      :data-period-index="idx"
                      :class="getPeriodRowClass(idx)"
                    >
                      <b-td class="text-center align-middle">{{ idx + 1 }}</b-td>
                      <b-td class="align-middle">
                        <b-form-select
                          v-model.number="period.month"
                          :options="monthOptions"
                          size="sm"
                          :disabled="formDisabled"
                          @change="onPeriodFieldChange(idx)"
                        />
                      </b-td>
                      <b-td class="align-middle">
                        <span
                          class="d-inline-block px-2 year-readonly"
                          :class="{ 'year-readonly--invalid': Number(period.year) !== currentYear }"
                        >{{ period.year }}</span>
                      </b-td>
                      <b-td
                        class="align-middle amount-cell"
                        :class="{ 'amount-cell--drag-source': dragState && dragState.sourceIndex === idx }"
                        @mousedown.capture="onAmountCellMouseDown(idx, $event)"
                      >
                        <div class="amount-cell__wrap">
                          <BInputNumber
                            v-model="period.amount"
                            placeholder="0"
                            size="sm"
                            :disabled="formDisabled"
                            @valueChange="onAmountChange(idx, $event)"
                          />
                        </div>
                        <small
                          v-if="periodErrors[idx]"
                          class="text-danger d-block mt-1"
                        >{{ periodErrors[idx] }}</small>
                      </b-td>
                      <b-td class="text-center align-middle">
                        <span
                          v-if="!formDisabled"
                          class="flaticon2-rubbish-bin-delete-button text-danger"
                          style="cursor:pointer; font-size:1rem"
                          @click="removePeriod(idx)"
                        />
                      </b-td>
                    </b-tr>
                    <b-tr v-if="!form.periods.length">
                      <b-td colspan="5" class="text-center text-muted py-3">
                        Chưa có kỳ nào. Bấm <strong>Thêm kỳ</strong> để bắt đầu.
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>

                <div class="d-flex align-items-center justify-content-between">
                  <b-button
                    size="sm"
                    variant="primary"
                    :disabled="formDisabled || form.periods.length >= PERIOD_CAP"
                    @click="addPeriod"
                  >
                    <i class="fas fa-plus"></i> Thêm kỳ
                  </b-button>
                  <small class="text-muted">
                    Tổng:
                    <strong class="text-primary">{{ convertPrice(totalAmount) }} đ</strong>
                  </small>
                </div>
                <small class="text-muted d-block mt-2">
                  <i class="fas fa-info-circle mr-1"></i>
                  Mẹo: Click và kéo từ ô số tiền để áp dụng giá trị xuống các dòng bên dưới.
                </small>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <!-- Lý do yêu cầu -->
              <b-col cols="12" md="8">
                <b-form-group>
                  <template #label>
                    Lý do yêu cầu <span class="text-danger">*</span>
                  </template>
                  <b-form-textarea
                    v-model="form.reason"
                    rows="3"
                    placeholder="Nhập lý do yêu cầu bổ sung ngân sách"
                    maxlength="1000"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Tệp đính kèm -->
            <b-row class="mb-4">
              <b-col cols="12" md="8">
                <b-form-group label="Tệp đính kèm">
                  <FileUploadManager
                    v-if="form.id"
                    :entity-id="form.id"
                    :entity="UPLOAD_ENTITY.BUDGET_LINE_REQUEST"
                    :disabled="isSaving"
                  />
                  <small v-else class="text-muted">Lưu phiếu trước để đính kèm tệp.</small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Người duyệt -->
            <b-row class="mb-4">
              <b-col cols="12" md="8">
                <ApprovalStepSearchSelector
                  :steps="approvalSteps"
                  emptyText="Chưa có cấu hình duyệt cho loại phiếu này."
                  @change="approvalSteps = $event"
                />
              </b-col>
            </b-row>

            <!-- Buttons -->
            <b-row>
              <b-col>
                <b-button
                  variant="primary"
                  :disabled="isSaving"
                  @click="onSaveDraft"
                >
                  <i class="fas fa-save"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu nháp' }}
                </b-button>
                <b-button
                  v-if="!isEdit"
                  variant="success"
                  class="ml-2"
                  :disabled="isSaving"
                  @click="onSaveAndSubmit"
                >
                  <i class="fas fa-paper-plane"></i>
                  {{ isSaving ? 'Đang xử lý...' : 'Lưu & gửi duyệt' }}
                </b-button>
                <b-button variant="secondary" class="ml-2" @click="goBack">
                  <i class="fas fa-arrow-left"></i> Quay lại
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { getData, getEmployeeInfoFromStorage } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
  xoa_dau,
} from '@/utils/common';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import ApprovalStepSearchSelector from '@/view/components/approval/ApprovalStepSearchSelector.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';
import BudgetLineRequestService from '@/services/budget-line-request.service';
import moment from 'moment';
import {
  APPROVAL_CONFIG_TYPE,
  UPLOAD_ENTITY,
  BUDGET_LINE_REQUEST_STATUS,
} from '@/utils/enum';
import { mergeApprovalConfigDetailsByStep } from '@/utils/approval-config';
import commonMixin from '@/view/mixins/api-common.mixins';

const PERIOD_CAP = 12;

export default {
  components: {
    KTCodePreview,
    Autosuggest,
    BInputNumber,
    ApprovalStepSearchSelector,
    FileUploadManager,
  },
  mixins: [commonMixin],
  beforeRouteEnter(_to, from, next) {
    next((vm) => { vm.returnQuery = from && from.name === 'list-budget-line-requests' ? { ...from.query } : {}; });
  },
  data() {
    return {
      UPLOAD_ENTITY,
      BUDGET_LINE_REQUEST_STATUS,
      PERIOD_CAP,
      // currentYear chốt tại thời điểm mount — chỉ cho phép bổ sung ngân
      // sách năm hiện tại (override 2026-06-18). KHÔNG cho năm trước/sau.
      currentYear: new Date().getFullYear(),
      isEdit: false,
      isSaving: false,
      returnQuery: {},
      departmentModel: '',
      storeModel: '',
      costItemModel: '',
      departmentSource: [],
      departmentSuggestions: [],
      storeSource: [],
      storeSuggestions: [],
      costItemSource: [],
      costItemSuggestions: [],
      form: {
        id: null,
        departmentId: null,
        costItemId: null,
        storeId: null,
        companyId: null,
        // periods: [{ month, year, amount, id? }] — id chỉ có khi edit
        periods: [],
        reason: '',
        status: null,
      },
      approvalSteps: [],
      suggestLimit: 50,
      companyOptions: [{ value: null, text: '-- Doanh nghiệp --' }],
      monthOptions: Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        text: `Tháng ${i + 1}`,
      })),
      // Drag-fill state. null = idle. Trong lúc drag confirmed:
      // { sourceIndex, sourceValue, targetIndex }
      dragState: null,
      dragCoveredSet: [],
      // Candidate drag (chưa pass threshold). null = idle.
      // { startX, startY, sourceIndex, sourceValue, hasDragged: false }
      dragCandidate: null,
      DRAG_THRESHOLD_PX: 5,
    };
  },
  computed: {
    totalAmount() {
      return (this.form.periods || []).reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0,
      );
    },
    formDisabled() {
      const s = this.form.status;
      if (!s) return false;
      return (
        s === BUDGET_LINE_REQUEST_STATUS.APPROVED ||
        s === BUDGET_LINE_REQUEST_STATUS.REJECTED
      );
    },
    // Cảnh báo defensive cho phiếu legacy: có kỳ ngoài năm hiện tại
    hasOutOfYearPeriods() {
      return (this.form.periods || []).some(
        (p) => Number(p.year) !== this.currentYear,
      );
    },
    // Map index → message khi (month,year) trùng dòng khác
    periodErrors() {
      const errors = {};
      const seen = {};
      (this.form.periods || []).forEach((p, idx) => {
        const key = `${p.year}-${p.month}`;
        if (seen[key] != null) {
          errors[idx] = `Trùng kỳ ${p.month}/${p.year} với dòng ${seen[key] + 1}`;
          if (errors[seen[key]] == null) {
            errors[seen[key]] = `Trùng kỳ ${p.month}/${p.year} với dòng ${idx + 1}`;
          }
        } else {
          seen[key] = idx;
        }
      });
      return errors;
    },
  },
  created() {
    const user = getData('user');
    this.form.companyId = (user && user.companyId) || null;
    this.fetchCompanies();
    this.fetchDepartments();
    this.fetchStores();
    this.fetchCostItems();
    this.fetchApprovalConfig();

    const q = this.$route.query;
    const id = q.id;
    if (id) {
      this.isEdit = true;
      this.fetchDetail(id);
    } else {
      // Pre-fill from query params (e.g. coming from budget-line detail).
      // Year luôn ép về currentYear — chỉ cho phép bổ sung ngân sách năm hiện tại.
      if (q.period) {
        const m = moment(q.period);
        if (m.isValid()) {
          this.form.periods.splice(0, this.form.periods.length, {
            month: m.month() + 1,
            year: this.currentYear,
            amount: 0,
          });
        }
      }
      if (q.departmentId) this.form.departmentId = Number(q.departmentId);
      if (q.costItemId) this.form.costItemId = Number(q.costItemId);
      if (q.storeId) this.form.storeId = Number(q.storeId);
      if (q.companyId) this.form.companyId = Number(q.companyId);
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Yêu cầu bổ sung NS', route: '/budget/budget-line-requests' },
      { title: this.isEdit ? 'Sửa' : 'Tạo mới' },
    ]);
  },
  beforeDestroy() {
    this.cleanupDragListeners();
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    convertPrice,

    // === Period CRUD (Rule 9: dùng splice/Vue.set cho reactivity) ===
    findPeriodIndex(month, year) {
      return (this.form.periods || []).findIndex(
        (p) => Number(p.month) === Number(month) && Number(p.year) === Number(year),
      );
    },

    // Auto-pick tháng kế tiếp TRONG năm hiện tại.
    // Pattern: bắt đầu từ tháng hiện tại → tăng dần → tới 12 → wrap về 1.
    // Trả null nếu đã đủ 12 tháng (caller xử lý toast).
    pickNextPeriod() {
      const year = this.currentYear;
      const now = new Date();
      let start;
      if (this.form.periods.length === 0) {
        start = now.getMonth() + 1;
      } else {
        // Nối tiếp sau kỳ cuối (chỉ xét tháng vì year đã chốt)
        const last = this.form.periods[this.form.periods.length - 1];
        start = Number(last.month) + 1;
        if (start > 12) start = 1;
      }
      // Quét vòng 12 tháng, ưu tiên slot trống đầu tiên từ `start`
      for (let i = 0; i < 12; i++) {
        const month = ((start - 1 + i) % 12) + 1;
        if (this.findPeriodIndex(month, year) < 0) {
          return { month, year };
        }
      }
      return null;
    },

    addPeriod() {
      if (this.form.periods.length >= PERIOD_CAP) {
        this.makeToastFaile(`Tối đa ${PERIOD_CAP} kỳ trên mỗi phiếu.`);
        return;
      }
      const next = this.pickNextPeriod();
      if (!next) {
        this.makeToastFaile(
          `Đã đủ 12 kỳ của năm ${this.currentYear}.`,
        );
        return;
      }
      this.form.periods.splice(this.form.periods.length, 0, {
        month: Number(next.month),
        year: Number(next.year),
        amount: 0,
      });
    },

    removePeriod(idx) {
      if (idx < 0 || idx >= this.form.periods.length) return;
      this.form.periods.splice(idx, 1);
    },

    onPeriodFieldChange(idx) {
      // Đảm bảo reactive khi v-model select cập nhật xong — chủ yếu hook
      // cho việc trigger lại periodErrors computed.
      const p = this.form.periods[idx];
      if (!p) return;
      this.$set(this.form.periods, idx, {
        ...p,
        month: Number(p.month),
        year: Number(p.year),
      });
    },

    getPeriodRowClass(idx) {
      const classes = [];
      if (this.periodErrors[idx]) classes.push('is-error');
      if (this.dragCoveredSet.indexOf(idx) !== -1) classes.push('is-drag-covered');
      return classes.join(' ');
    },

    onAmountChange(idx, value) {
      const p = this.form.periods[idx];
      if (!p) return;
      const numeric = Number(value) || 0;
      if (p.amount === numeric) return;
      this.$set(this.form.periods, idx, { ...p, amount: numeric });
    },

    // === Drag-fill (Excel-style) ===
    // 2-phase: mousedown chỉ tạo candidate (không preventDefault để input
    // vẫn nhận focus như bình thường). Chỉ khi user move > threshold thì
    // chuyển sang drag mode thật sự — blur input, set cursor body.
    onAmountCellMouseDown(sourceIndex, event) {
      if (this.formDisabled) return;
      // Chỉ xử lý chuột trái
      if (event && typeof event.button === 'number' && event.button !== 0) return;
      const source = this.form.periods[sourceIndex];
      if (!source) return;
      this.dragCandidate = {
        startX: event.clientX,
        startY: event.clientY,
        sourceIndex,
        // Snapshot value tại thời điểm mousedown
        sourceValue: Number(source.amount) || 0,
        hasDragged: false,
      };
      document.addEventListener('mousemove', this.onAmountDragMove);
      document.addEventListener('mouseup', this.onAmountDragEnd);
      // KHÔNG preventDefault ở đây — để input nhận focus bình thường nếu
      // user chỉ click (không drag).
    },

    onAmountDragMove(event) {
      const candidate = this.dragCandidate;
      if (!candidate) return;

      if (!candidate.hasDragged) {
        const dx = event.clientX - candidate.startX;
        const dy = event.clientY - candidate.startY;
        if (Math.hypot(dx, dy) < this.DRAG_THRESHOLD_PX) return;
        // Chuyển sang confirmed drag
        candidate.hasDragged = true;
        this.dragState = {
          sourceIndex: candidate.sourceIndex,
          sourceValue: candidate.sourceValue,
          targetIndex: candidate.sourceIndex,
        };
        this.dragCoveredSet = [candidate.sourceIndex];
        // Blur input đang focus (nếu là input trong cell) để không cản drag
        if (
          document.activeElement &&
          typeof document.activeElement.blur === 'function' &&
          document.activeElement.tagName === 'INPUT'
        ) {
          document.activeElement.blur();
        }
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'crosshair';
      }

      // Pass threshold rồi: ngăn text selection drag mặc định
      if (event.preventDefault) event.preventDefault();

      const el = document.elementFromPoint(event.clientX, event.clientY);
      if (!el) return;
      const row = el.closest && el.closest('tr[data-period-index]');
      if (!row) return;
      const targetIdx = Number(row.getAttribute('data-period-index'));
      if (Number.isNaN(targetIdx)) return;
      if (!this.dragState || targetIdx === this.dragState.targetIndex) return;
      this.dragState.targetIndex = targetIdx;
      const src = this.dragState.sourceIndex;
      const lo = Math.min(src, targetIdx);
      const hi = Math.max(src, targetIdx);
      const covered = [];
      for (let i = lo; i <= hi; i++) covered.push(i);
      this.dragCoveredSet = covered;
    },

    onAmountDragEnd() {
      const candidate = this.dragCandidate;
      // Click thuần (không pass threshold) → không làm gì, để input
      // tự nhận focus như bình thường.
      if (!candidate || !candidate.hasDragged) {
        this.cleanupDragListeners();
        return;
      }
      if (this.dragState) {
        const { sourceIndex, sourceValue } = this.dragState;
        const covered = this.dragCoveredSet.slice();
        covered.forEach((idx) => {
          if (idx === sourceIndex) return;
          const p = this.form.periods[idx];
          if (!p) return;
          this.$set(this.form.periods, idx, {
            ...p,
            amount: Number(sourceValue) || 0,
          });
        });
      }
      this.cleanupDragListeners();
    },

    cleanupDragListeners() {
      document.removeEventListener('mousemove', this.onAmountDragMove);
      document.removeEventListener('mouseup', this.onAmountDragEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      this.dragState = null;
      this.dragCoveredSet = [];
      this.dragCandidate = null;
    },

    fetchCompanies() {
      ApiService.get('/company')
        .then(({ data }) => {
          const rows = data.data || [];
          this.companyOptions = [
            { value: null, text: '-- Doanh nghiệp --' },
            ...rows.map((c) => ({ value: c.id, text: c.name })),
          ];
        })
        .catch(() => {});
    },
    filterSuggestionsByName(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) return list.slice(0, limit);
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          return (
            xoa_dau(name).toLowerCase().indexOf(tLower) > -1 ||
            name.toLowerCase().indexOf(tLower) > -1
          );
        })
        .slice(0, limit);
    },
    filterStoreSuggestions(source, text, limit) {
      const list = Array.isArray(source) ? source : [];
      const t = text != null ? String(text) : '';
      if (!t.trim()) return list.slice(0, limit);
      const tLower = t.toLowerCase();
      return list
        .filter((item) => {
          const name = item && item.name != null ? String(item.name) : '';
          const shortName =
            item && item.shortName != null ? String(item.shortName) : '';
          return [name, shortName].filter(Boolean).some((f) => {
            return (
              xoa_dau(f).toLowerCase().indexOf(tLower) > -1 ||
              f.toLowerCase().indexOf(tLower) > -1
            );
          });
        })
        .slice(0, limit);
    },
    syncDepartmentSuggestionsFromModel() {
      this.departmentSuggestions = this.filterSuggestionsByName(
        this.departmentSource,
        this.departmentModel,
        this.suggestLimit,
      );
    },
    syncCostItemSuggestionsFromModel() {
      this.costItemSuggestions = this.filterSuggestionsByName(
        this.costItemSource,
        this.costItemModel,
        this.suggestLimit,
      );
    },
    syncStoreSuggestionsFromModel() {
      this.storeSuggestions = this.filterStoreSuggestions(
        this.storeSource,
        this.storeModel,
        this.suggestLimit,
      );
    },
    fetchDepartments() {
      ApiService.query('departments/all')
        .then(({ data }) => {
          const rows = data.data || [];
          this.departmentSource = rows.map((d) => ({
            id: Number(d.id),
            name: d.name,
          }));
          if (this.form.departmentId && !this.departmentModel) {
            const found = this.departmentSource.find(
              (d) => d.id === Number(this.form.departmentId),
            );
            if (found) this.departmentModel = found.name;
          }
          this.syncDepartmentSuggestionsFromModel();
        })
        .catch(() => {});
    },
    fetchStores() {
      this.fetchStoresByUser()
        .then((data) => {
          const stores = data.data.stores || [];
          this.storeSource = stores.map((s) => ({
            id: Number(s.id),
            name: s.name,
            shortName: s.shortName || '',
          }));
          if (this.form.storeId && !this.storeModel) {
            const found = this.storeSource.find(
              (s) => s.id === Number(this.form.storeId),
            );
            if (found)
              this.storeModel = found.shortName
                ? `${found.name} (${found.shortName})`
                : found.name;
          }
          this.syncStoreSuggestionsFromModel();
        })
        .catch(() => {});
    },
    fetchCostItems() {
      ApiService.get('v2/cost-items/all')
        .then(({ data }) => {
          const rows = data.data || [];
          this.costItemSource = rows.map((c) => ({
            id: Number(c.id),
            name: c.name,
          }));
          if (this.form.costItemId && !this.costItemModel) {
            const found = this.costItemSource.find(
              (c) => c.id === Number(this.form.costItemId),
            );
            if (found) this.costItemModel = found.name;
          }
          this.syncCostItemSuggestionsFromModel();
        })
        .catch(() => {});
    },
    mapApprovalSteps(details, selectedApprovers = []) {
      const selectedByStep = (selectedApprovers || []).reduce((acc, item) => {
        acc[item.step] = item.approverBy;
        return acc;
      }, {});
      const merged = mergeApprovalConfigDetailsByStep(details || []);
      this.approvalSteps = merged.map((detail) => ({
        step: detail.step,
        approverBy: selectedByStep[detail.step] || null,
        owners: (detail.owners || [])
          .filter((o) => o && o.id)
          .filter(
            (o, idx, arr) =>
              arr.findIndex((x) => Number(x.id) === Number(o.id)) === idx,
          )
          .map((o) => ({
            id: Number(o.id),
            name: o.name || o.fullName || String(o.id),
            code: o.code || '',
          })),
      }));
    },
    fetchApprovalConfig(selectedApprovers = []) {
      ApiService.get(
        `v2/approval-configs/${APPROVAL_CONFIG_TYPE.BUDGET_REQUEST}/approvers`,
      )
        .then(({ data }) => {
          if (data.status === 200 && data.data) {
            this.mapApprovalSteps(data.data.details || [], selectedApprovers);
          } else {
            this.approvalSteps = [];
          }
        })
        .catch(() => {
          this.approvalSteps = [];
        });
    },
    fetchDetail(id) {
      BudgetLineRequestService.getById(id)
        .then((req) => {
          if (!req) return;
          const currentEmployeeId = getEmployeeInfoFromStorage()?.id;
          if (
            currentEmployeeId &&
            req.createdBy &&
            Number(currentEmployeeId) !== Number(req.createdBy)
          ) {
            this.$router.replace({
              name: 'detail-budget-line-request',
              query: { id },
            });
            return;
          }
          this.form.id = req.id || null;
          this.form.departmentId = req.departmentId || null;
          this.form.costItemId = req.costItemId || null;
          this.form.storeId = req.storeId || null;
          this.form.companyId = req.companyId || this.form.companyId;
          this.form.reason = req.reason || '';
          this.form.status = req.status || null;
          // splice để đảm bảo reactivity (Rule 9)
          this.form.periods.splice(
            0,
            this.form.periods.length,
            ...(req.periods || []).map((p) => ({
              id: p.id || null,
              month: Number(p.month),
              year: Number(p.year),
              amount: Number(p.amount) || 0,
            })),
          );

          if (req.departmentName) this.departmentModel = req.departmentName;
          if (req.storeName) this.storeModel = req.storeName;
          if (req.costItemName) this.costItemModel = req.costItemName;

          this.fetchApprovalConfig(req.approvers || []);

          this.$nextTick(() => {
            this.syncDepartmentSuggestionsFromModel();
            this.syncCostItemSuggestionsFromModel();
            this.syncStoreSuggestionsFromModel();
          });
        })
        .catch((err) => handleError(err));
    },

    onInputDepartment(text) {
      this.departmentModel = (text || '').toString();
      if (!this.departmentModel.trim()) this.form.departmentId = null;
      this.syncDepartmentSuggestionsFromModel();
    },
    onSelectedDepartment(option) {
      const item = option && option.item ? option.item : null;
      this.form.departmentId = item ? Number(item.id) : null;
      this.departmentModel = item ? item.name : '';
      this.departmentSuggestions = this.departmentSource.slice(
        0,
        this.suggestLimit,
      );
    },

    onInputStore(text) {
      this.storeModel = (text || '').toString();
      if (!this.storeModel.trim()) this.form.storeId = null;
      this.syncStoreSuggestionsFromModel();
    },
    onSelectedStore(option) {
      const item = option && option.item ? option.item : null;
      this.form.storeId = item ? Number(item.id) : null;
      this.storeModel = item
        ? item.shortName
          ? `${item.name} (${item.shortName})`
          : item.name
        : '';
      this.storeSuggestions = this.storeSource.slice(0, this.suggestLimit);
    },

    onInputCostItem(text) {
      this.costItemModel = (text || '').toString();
      if (!this.costItemModel.trim()) this.form.costItemId = null;
      this.syncCostItemSuggestionsFromModel();
    },
    onSelectedCostItem(option) {
      const item = option && option.item ? option.item : null;
      this.form.costItemId = item ? Number(item.id) : null;
      this.costItemModel = item ? item.name : '';
      this.costItemSuggestions = this.costItemSource.slice(
        0,
        this.suggestLimit,
      );
    },

    validate({ requirePositive = false } = {}) {
      if (!this.form.companyId) {
        this.makeToastFaile('Vui lòng chọn doanh nghiệp!');
        return false;
      }
      if (!this.form.departmentId) {
        this.makeToastFaile('Vui lòng chọn phòng ban!');
        return false;
      }
      if (!this.form.costItemId) {
        this.makeToastFaile('Vui lòng chọn khoản mục phí!');
        return false;
      }
      if (!Array.isArray(this.form.periods) || this.form.periods.length === 0) {
        this.makeToastFaile('Vui lòng thêm ít nhất 1 kỳ.');
        return false;
      }
      if (this.form.periods.length > PERIOD_CAP) {
        this.makeToastFaile(`Tối đa ${PERIOD_CAP} kỳ/phiếu.`);
        return false;
      }
      const seen = new Set();
      for (const p of this.form.periods) {
        if (Number(p.year) !== this.currentYear) {
          this.makeToastFaile(
            `Chỉ cho phép bổ sung ngân sách năm ${this.currentYear}.`,
          );
          return false;
        }
        const key = `${p.year}-${p.month}`;
        if (seen.has(key)) {
          this.makeToastFaile(`Trùng kỳ ${p.month}/${p.year}.`);
          return false;
        }
        seen.add(key);
        if (Number(p.amount) < 0) {
          this.makeToastFaile(`Số tiền kỳ ${p.month}/${p.year} không hợp lệ.`);
          return false;
        }
        if (requirePositive && Number(p.amount) <= 0) {
          this.makeToastFaile(
            `Số tiền kỳ ${p.month}/${p.year} phải lớn hơn 0 trước khi gửi duyệt.`,
          );
          return false;
        }
      }
      if (!this.form.reason || !this.form.reason.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do yêu cầu!');
        return false;
      }
      const invalidStep = this.approvalSteps.find((s) => !s.approverBy);
      if (invalidStep) {
        this.makeToastFaile(
          `Vui lòng chọn người duyệt cho bước ${invalidStep.step}!`,
        );
        return false;
      }
      return true;
    },

    buildPayload() {
      return {
        departmentId: this.form.departmentId,
        costItemId: this.form.costItemId,
        storeId: this.form.storeId || null,
        companyId: this.form.companyId,
        reason: this.form.reason,
        periods: this.form.periods.map((p) => ({
          month: Number(p.month),
          year: Number(p.year),
          amount: Number(p.amount) || 0,
        })),
        approvers: this.approvalSteps.map((s) => ({
          step: s.step,
          approverBy: s.approverBy,
        })),
      };
    },

    onSaveDraft() {
      if (!this.validate()) return;
      this.isSaving = true;
      const payload = this.buildPayload();
      const action = this.isEdit
        ? BudgetLineRequestService.update(this.$route.query.id, payload)
        : BudgetLineRequestService.create(payload);

      action
        .then(({ message, data }) => {
          this.makeToastSuccess(message || 'Lưu thành công');
          if (this.isEdit) {
            const id = (data && data.id) || this.$route.query.id;
            this.$router.push({
              name: 'detail-budget-line-request',
              query: { id },
            });
          } else {
            this.$router.push({
              name: 'list-budget-line-requests',
              query: { ...this.returnQuery, sortBy: 'createdAt' },
            });
          }
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isSaving = false;
        });
    },

    onSaveAndSubmit() {
      if (!this.validate({ requirePositive: true })) return;
      this.isSaving = true;
      const payload = this.buildPayload();
      BudgetLineRequestService.submitWithDuplicateCheck({
        payload: {
          companyId: payload.companyId,
          departmentId: payload.departmentId,
          storeId: payload.storeId,
          costItemId: payload.costItemId,
          periods: payload.periods.map((p) => ({ month: p.month, year: p.year })),
          excludeRequestId: this.isEdit ? Number(this.$route.query.id) : undefined,
        },
        confirmWarningFn: (conflicts) => {
          const list = (conflicts || [])
            .map((c) => `${c.code || `#${c.id}`} (${c.status})`)
            .join(', ');
          return this.$bvModal.msgBoxConfirm(
            `Có phiếu đã duyệt trùng kỳ: ${list}. Bạn vẫn muốn tiếp tục gửi duyệt?`,
            {
              title: 'Cảnh báo trùng phiếu',
              okVariant: 'warning',
              okTitle: 'Tiếp tục',
              cancelTitle: 'Hủy',
            },
          );
        },
        submitFn: async () => {
          const createRes = await BudgetLineRequestService.create(payload);
          const id = createRes.data && createRes.data.id;
          if (!id) {
            return createRes;
          }
          const submitRes = await BudgetLineRequestService.submit(id);
          return { ...submitRes, id };
        },
      })
        .then(({ result }) => {
          const id = result.id || (result.data && result.data.id);
          this.makeToastSuccess(result.message || 'Đã submit thành công');
          if (id) {
            this.$router.push({
              name: 'detail-budget-line-request',
              query: { id },
            });
          } else {
            this.$router.push({
              name: 'list-budget-line-requests',
              query: { ...this.returnQuery, sortBy: 'createdAt' },
            });
          }
        })
        .catch((err) => {
          if (err && err.code === 'CANCELLED_BY_USER') {
            return;
          }
          if (err && err.code === 'DUPLICATE_PENDING_REQUEST') {
            const conflicts =
              (err.details && err.details.conflictingRequests) || [];
            const list = conflicts
              .map((c) => `${c.code || `#${c.id}`}`)
              .join(', ');
            this.makeToastFaile(
              `Đã có phiếu trùng đang chờ duyệt: ${list}. Vui lòng kiểm tra lại.`,
            );
            return;
          }
          handleError(err);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    goBack() {
      this.$router.push({ name: 'list-budget-line-requests', query: { ...this.returnQuery } }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.period-table th,
.period-table td {
  vertical-align: middle;
}

.period-table tr.is-error > td {
  background-color: #fff3f3;
}

.period-table tr.is-drag-covered > td {
  background-color: #f0f9ff;
}

/* Excel-style: toàn bộ ô amount mặc định cursor crosshair (`+`).
   Input bên trong kế thừa cursor crosshair khi chưa focus, chuyển thành
   text cursor khi đang focus để không phá UX editing. */
.amount-cell {
  position: relative;
  cursor: crosshair;
}

.amount-cell__wrap {
  position: relative;
}

.amount-cell input {
  cursor: crosshair;
}

.amount-cell input:focus {
  cursor: text;
}

.amount-cell--drag-source {
  background-color: #e6f4ff;
}

/* Năm hiển thị plain text (đã readonly — chỉ năm hiện tại) */
.year-readonly {
  font-weight: 500;
  color: #495057;
}

.year-readonly--invalid {
  color: #dc3545;
  text-decoration: line-through;
}
</style>
